import { forwardRef, type ReactNode } from 'react';

import {
  _canReceiveDefaultTextFormat,
  type DataTestId,
  type StylingProps,
} from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';
import { Text } from '@dynatrace/strato-components/typography';

//#region Typings
/**
 * Accepted properties for the MessageContainer Description.
 * @public
 */
export interface MessageContainerDescriptionProps
  extends StylingProps,
    DataTestId {
  /** Elements to be displayed in the description slot. */
  children: ReactNode;
}
//#endregion

/**
 * The MessageContainer Description component.
 * @public
 */
export const Description = forwardRef<
  HTMLDivElement,
  MessageContainerDescriptionProps
>((props, forwardedRef) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    ...remainingProps
  }: MessageContainerDescriptionProps = props;

  const childIsText = _canReceiveDefaultTextFormat(children);

  return (
    <Flex
      data-testid={dataTestId}
      ref={forwardedRef}
      className={consumerClassName}
      style={consumerStyle}
      {...remainingProps}
    >
      {childIsText ? <Text textStyle="base">{children}</Text> : children}
    </Flex>
  );
});

(Description as typeof Description & { displayName: string }).displayName =
  'MessageContainer.Description';
